import React from "react";
import { useTranslation } from "react-i18next";
import { useGetCartQuery } from "../../api/cartApi";
import CartPanelItem from "./CartPanelItem";
import CartPanelItemSkeleton from "./CartPanelItemSkeleton";
import FreeShippingProgressBar from "../common/FreeShippingProgressBar";
import FirstPurchaseBanner from "../common/FirstPurchaseBanner";
import CartTotals from "../common/CartTotals";
import PanelFeaturedProducts from "../../../featured/components/Panel/PanelFeaturedProducts";
import PanelFeaturedCategories from "../../../featured/components/Panel/PanelFeaturedCategories";
import PanelFeaturedTags from "../../../featured/components/Panel/PanelFeaturedTags";
import PanelSavedForLaterList from "../../../savedForLater/components/Panel/PanelSavedForLaterList";
import DeliveryEstimateDisplay from "../../../product/components/common/DeliveryEstimateDisplay";

const CartPanel: React.FC = () => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const { data: cart, isLoading, isError } = useGetCartQuery();

  // --- Standardized Render Logic ---

  // Create a combined loading state
  const isComponentLoading = isLoading || !areTranslationsReady;

  // State 1: Loading (show skeleton)
  if (isComponentLoading) {
    return (
      <div className="cart-panel">
        <div className="cart-panel__body">
          <ul className="cart-panel__items">
            {Array.from({ length: 3 }).map((_, index) => (
              <CartPanelItemSkeleton key={index} />
            ))}
          </ul>
        </div>
      </div>
    );
  }

  // State 2: Error
  if (isError) {
    return (
      <div className="cart-panel">
        <p className="cart-panel__message cart-panel__message--error">
          {t("cartOffcanvas.error", "Error loading cart.")}
        </p>
      </div>
    );
  }

  // State 3: Empty Cart
  if (!cart || cart.itemsCount === 0) {
    return (
      <div className="cart-panel">
        <div className="cart-panel__body">
          <p className="cart-panel__message cart-panel__message--empty">
            {t("cartOffcanvas.empty", "Your cart is empty.")}
          </p>

          {/* Saved For Later Items (Visible even when cart is empty, above featured) */}
          <PanelSavedForLaterList />

          <PanelFeaturedProducts />
          <PanelFeaturedCategories />
          <PanelFeaturedTags />
        </div>
      </div>
    );
  }

  // State 5: Success with Results
  return (
    <div className="cart-panel">
      <div className="cart-panel__header">
        {/* Only show Shipping Progress if physical items exist */}
        {cart.needsShipping && <FreeShippingProgressBar />}

        {/* Show First Purchase Banner (Subtle variant) */}
        <FirstPurchaseBanner context="panel" />

        {/* Only show delivery estimate if physical items exist */}
        {cart.needsShipping && (
          <DeliveryEstimateDisplay
            estimate={cart.deliveryEstimation}
            className="cart-panel__delivery-estimate"
          />
        )}
      </div>
      <div className="cart-panel__body">
        {/* State 4: Fetching, we intentionally ignore this state to avoid flicker */}
        <ul className="cart-panel__items">
          {cart.items.map((item) => (
            <CartPanelItem key={item.key} item={item} />
          ))}
        </ul>

        {/* Show Saved Items below cart items when cart is populated */}
        <PanelSavedForLaterList />
      </div>
      <div className="cart-panel__footer">
        <CartTotals cart={cart} context="panel" showViewCartButton={true} />
      </div>
    </div>
  );
};

export default CartPanel;
